import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, ChevronRight, ChevronLeft, ShieldCheck, Truck, Sparkles, Zap } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { SHOP_HERO, SHOP_TRUST, getCategoryContent, getProductImage } from "@/lib/shop-content";
import ShopPromoBanner from "@/components/ShopPromoBanner";
import Autocomplete from "@/components/Autocomplete";
import { breadcrumbLd, productItemListLd } from "@/lib/schema-org";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Storefront landing / category browse — image-rich, SinaLite-style layout.
 * - No ?category  → hero + category grid + trust strip + "add value" section
 * - ?category=... → category hero + product grid
 */
export default function Shop() {
  const [params, setParams] = useSearchParams();
  const activeCategory = params.get("category") || null;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // JSON-LD — BreadcrumbList always, ItemList when we have products loaded.
  // Product cards use owner_photo_url which is relative (`/api/…`) so we
  // pass REACT_APP_BACKEND_URL to resolve full-qualified URLs.
  const jsonLd = useMemo(() => {
    const crumbs = [{ name: "Home", path: "/" }, { name: "Print Shop", path: "/shop" }];
    if (activeCategory) {
      crumbs.push({ name: activeCategory, path: `/shop?category=${encodeURIComponent(activeCategory)}` });
    }
    const schemas = [breadcrumbLd(crumbs)];
    if (products.length > 0) {
      schemas.push(productItemListLd({
        items: products,
        origin: window.location.origin,
        backendUrl: process.env.REACT_APP_BACKEND_URL,
        name: activeCategory ? `${activeCategory} — Print Shop` : "Print Shop products",
      }));
    }
    return schemas;
  }, [activeCategory, products]);

  useSEO({
    title: activeCategory
      ? `${activeCategory} — Order online`
      : "Print Shop — Business Cards, Flyers, Banners & More",
    description: activeCategory
      ? `Wholesale ${activeCategory.toLowerCase()} printing with live pricing and Canada-wide shipping.`
      : "Order custom printing online: business cards, flyers, brochures, banners and signs. Live pricing, fast turnaround, Canada-wide shipping.",
    path: activeCategory ? `/shop?category=${encodeURIComponent(activeCategory)}` : "/shop",
    jsonLd,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [pRes, cRes] = await Promise.all([
          axios.get(`${API}/sinalite/products${activeCategory ? `?category=${encodeURIComponent(activeCategory)}` : ""}`),
          axios.get(`${API}/sinalite/categories`),
        ]);
        if (!cancelled) {
          setProducts(pRes.data.items || []);
          setCategories(cRes.data.categories || []);
        }
      } catch {
        if (!cancelled) { setProducts([]); setCategories([]); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [activeCategory]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [products, search]);

  return (
    <div className="bg-ink-950 min-h-screen" data-testid="shop-page">
      <ShopPromoBanner />
      {activeCategory ? (
        <CategoryView
          category={activeCategory}
          products={filtered}
          allCategories={categories}
          loading={loading}
          search={search}
          onSearch={setSearch}
          onClear={() => setParams({})}
        />
      ) : (
        <LandingView
          categories={categories}
          products={products}
          loading={loading}
          search={search}
          onSearch={setSearch}
          onSelectCategory={(c) => setParams({ category: c })}
        />
      )}
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Landing view — hero + category cards + trust strip                        */
/* -------------------------------------------------------------------------- */

function LandingView({ categories, products, loading, search, onSearch, onSelectCategory }) {
  // Only show categories that have at least one visible product
  const productsByCategory = useMemo(() => {
    const m = {};
    for (const p of products) {
      if (!m[p.category]) m[p.category] = [];
      m[p.category].push(p);
    }
    return m;
  }, [products]);
  const visibleCategories = categories.filter((c) => (productsByCategory[c] || []).length > 0);

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden grain" data-testid="shop-hero">
        <div className="absolute inset-0">
          <img src={SHOP_HERO.image} alt="Business cards fanned out" className="w-full h-full object-cover opacity-30" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/90 to-ink-950/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-ink-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 pb-24">
          <div className="max-w-3xl">
            <div className="eyebrow gold-bar">{SHOP_HERO.eyebrow}</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-7 tracking-tight leading-[1.02]" data-testid="shop-title">
              {SHOP_HERO.title.split("—")[0]}
              <span className="italic text-gold">— with live pricing.</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed mt-7 max-w-2xl">{SHOP_HERO.subtitle}</p>
            <ul className="mt-9 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-[13px] text-white/75 max-w-2xl">
              {SHOP_HERO.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2"><Sparkles size={13} className="text-gold mt-0.5 shrink-0" /> {b}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 -mt-8 relative z-10">
        <Autocomplete
          variant="compact"
          testId="shop-search"
          placeholder="Search products — business cards, flyers, banners…"
          value={search}
          onChange={onSearch}
          onQuery={async (q) => {
            const query = q.toLowerCase();
            return products
              .filter((p) => (p.name || "").toLowerCase().includes(query) || (p.category || "").toLowerCase().includes(query) || (p.sku || "").toLowerCase().includes(query))
              .slice(0, 8)
              .map((p) => ({
                type: "product",
                id: p.id,
                title: p.name,
                subtitle: p.category,
                url: `/shop/product/${p.id}`,
                image: p.owner_photo_url || getProductImage(p),
              }));
          }}
          noResultsHint="No products match — try 'business cards', 'flyers', or 'stickers'."
        />
      </section>

      {/* SEARCH RESULTS */}
      {search.trim() ? (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="search-results">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-6" data-testid="product-count">
            {products.filter((p) => (p.name || "").toLowerCase().includes(search.trim().toLowerCase())).length} product(s) matching &ldquo;{search}&rdquo;
          </div>
          <ProductGrid products={products.filter((p) => (p.name || "").toLowerCase().includes(search.trim().toLowerCase()))} />
        </section>
      ) : (
        <>
          {/* CATEGORY GRID */}
          <section className="max-w-7xl mx-auto px-6 md:px-10 py-16" data-testid="category-grid">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="eyebrow gold-bar">Shop by category</div>
                <h2 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">Browse the catalogue</h2>
              </div>
            </div>
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-72 bg-ink-900 border border-white/5 animate-pulse rounded-sm" />)}
              </div>
            ) : visibleCategories.length === 0 ? (
              <div className="card-dark p-16 text-center text-white/50" data-testid="shop-empty-categories">
                No products are live on the shop yet. Please check back soon.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {visibleCategories.map((c) => {
                  const content = getCategoryContent(c);
                  const items = productsByCategory[c] || [];
                  // Use the first product in the category as the tile image —
                  // prefer products with an actual owner photo so we never render
                  // the generic hardcoded fallback when a real photo is available.
                  const photoProducts = items.filter((p) => p.owner_photo_url);
                  const heroProduct = photoProducts[0] || items[0] || null;
                  const tileImage = heroProduct ? getProductImage(heroProduct) : content.image;
                  return (
                    <button
                      key={c}
                      data-testid={`category-tile-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => onSelectCategory(c)}
                      className="text-left card-dark overflow-hidden hover:border-gold/50 transition-colors group flex flex-col"
                    >
                      <div className="aspect-[4/3] bg-ink-800 relative overflow-hidden">
                        <img
                          src={tileImage}
                          alt={heroProduct?.seo_alt_text || c}
                          className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 to-transparent" />
                        <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-gold px-2 py-1 border border-gold/40 bg-black/40">{items.length} product{items.length !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="font-display text-xl text-white group-hover:text-gold transition-colors">{c}</div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-gold mt-1">{content.tagline}</div>
                        <p className="text-white/60 text-sm mt-3 leading-relaxed line-clamp-2 flex-1">{content.description}</p>
                        <div className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/50 group-hover:text-gold transition-colors flex items-center gap-1">
                          Shop {c.toLowerCase()} <ChevronRight size={12} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* TRUST STRIP */}
          <section className="bg-gradient-to-b from-ink-950 via-ink-900/60 to-ink-950 border-y border-white/5 py-16" data-testid="shop-trust">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { icon: <ShieldCheck size={22} className="text-gold" />, ...SHOP_TRUST[0] },
                  { icon: <Zap size={22} className="text-gold" />, ...SHOP_TRUST[2] },
                  { icon: <Truck size={22} className="text-gold" />, ...SHOP_TRUST[1] },
                ].map((t) => (
                  <div key={t.title} className="flex items-start gap-4">
                    <div className="p-3 border border-gold/30 bg-gold/5 rounded-sm shrink-0">{t.icon}</div>
                    <div>
                      <div className="font-display text-lg text-white">{t.title}</div>
                      <p className="text-white/60 text-sm mt-2 leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </>
  );
}


/* -------------------------------------------------------------------------- */
/*  Category view — hero + product grid + related sub-categories              */
/* -------------------------------------------------------------------------- */

function CategoryView({ category, products, allCategories, loading, search, onSearch, onClear }) {
  const content = getCategoryContent(category);
  return (
    <>
      {/* CATEGORY HERO */}
      <section className="relative overflow-hidden border-b border-white/5" data-testid="category-hero">
        <div className="absolute inset-0">
          <img src={content.image} alt={category} className="w-full h-full object-cover opacity-15" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div className="absolute inset-0 bg-gradient-to-b from-ink-950 via-ink-950/95 to-ink-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-14">
          <nav className="text-[10px] uppercase tracking-[0.24em] text-white/40 flex items-center gap-2 mb-6">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={11} />
            <button onClick={onClear} className="hover:text-gold">Print Shop</button>
            <ChevronRight size={11} />
            <span className="text-gold">{category}</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 items-center">
            <div>
              <div className="eyebrow gold-bar">{content.tagline}</div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight leading-[1.05]" data-testid="shop-title">
                Our Wholesale <span className="italic text-gold">{category}</span>
              </h1>
              <p className="text-white/70 text-base mt-5 max-w-2xl leading-relaxed">{content.description}</p>
            </div>
            <div className="hidden lg:block">
              <div className="aspect-square bg-ink-900 border border-white/5 overflow-hidden rounded-sm">
                <img src={content.image} alt={category} className="w-full h-full object-contain p-8" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEARCH + PRODUCT COUNT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <button onClick={onClear} data-testid="back-to-shop" className="text-[11px] uppercase tracking-[0.22em] text-white/50 hover:text-gold flex items-center gap-1">
            <ChevronLeft size={12} /> All categories
          </button>
          <div className="w-full md:w-80">
            <Autocomplete
              variant="compact"
              testId="shop-search"
              placeholder={`Search ${category.toLowerCase()}…`}
              value={search}
              onChange={onSearch}
              onQuery={async (q) => {
                const query = q.toLowerCase();
                return products
                  .filter((p) => (p.name || "").toLowerCase().includes(query) || (p.sku || "").toLowerCase().includes(query))
                  .slice(0, 8)
                  .map((p) => ({
                    type: "product",
                    id: p.id,
                    title: p.name,
                    subtitle: p.category,
                    url: `/shop/product/${p.id}`,
                    image: p.owner_photo_url || getProductImage(p),
                  }));
              }}
              noResultsHint="No products in this category match."
            />
          </div>
        </div>

        {/* PRODUCT GRID */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-96 bg-ink-900 border border-white/5 animate-pulse rounded-sm" />)}
          </div>
        ) : products.length === 0 ? (
          <div className="card-dark p-16 text-center text-white/50" data-testid="no-products">
            {search ? `No products in "${category}" match "${search}".` : `No products in this category are live yet.`}
          </div>
        ) : (
          <>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 mb-6" data-testid="product-count">
              {products.length} product{products.length !== 1 ? "s" : ""}
            </div>
            <ProductGrid products={products} />
          </>
        )}
      </section>

      {/* FEATURES */}
      {content.features && content.features.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 mt-10" data-testid="category-features">
          <div className="text-center mb-14">
            <div className="eyebrow gold-bar mx-auto w-fit">Why customers love our {category.toLowerCase()}</div>
            <h2 className="font-display text-3xl md:text-4xl mt-4 tracking-tight">The little details matter</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.features.map((f) => (
              <div key={f.title} className="card-dark p-8">
                <div className="w-10 h-10 border border-gold/30 bg-gold/5 rounded-sm flex items-center justify-center mb-5">
                  <Sparkles size={16} className="text-gold" />
                </div>
                <div className="font-display text-xl text-white">{f.title}</div>
                <p className="text-white/60 text-sm mt-3 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RELATED CATEGORIES */}
      {allCategories.length > 1 && (
        <section className="bg-ink-900/40 border-t border-white/5 py-14" data-testid="related-categories">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/40 mb-5">Explore more categories</div>
            <div className="flex flex-wrap gap-2">
              {allCategories.filter((c) => c !== category).slice(0, 20).map((c) => (
                <Link
                  key={c}
                  to={`/shop?category=${encodeURIComponent(c)}`}
                  data-testid={`related-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-white/10 text-white/70 hover:border-gold hover:text-gold transition-colors"
                >{c}</Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}


/* -------------------------------------------------------------------------- */
/*  Product card grid                                                          */
/* -------------------------------------------------------------------------- */

function ProductGrid({ products }) {
  if (products.length === 0) {
    return <div className="card-dark p-14 text-center text-white/50">No products match.</div>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
      {products.map((p) => (
        <Link
          key={p.id}
          to={`/shop/product/${p.id}`}
          data-testid={`product-card-${p.id}`}
          className="card-dark overflow-hidden hover:border-gold/50 transition-colors group flex flex-col"
        >
          <div className="aspect-[4/3] bg-ink-800 relative overflow-hidden">
            <img
              src={getProductImage(p)}
              alt={p.seo_alt_text || p.name}
              className="w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 to-transparent pointer-events-none" />
          </div>
          <div className="p-6 flex-1 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.24em] text-gold mb-2">{p.category}</div>
            <div className="font-display text-lg text-white group-hover:text-gold transition-colors leading-tight">{p.name.trim()}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-2">#{p.id} · {p.sku}</div>
            <div className="mt-auto pt-5 border-t border-white/5 flex items-center justify-between">
              <span className="text-[11px] uppercase tracking-[0.22em] text-white/60">Configure & price</span>
              <ShoppingBag size={14} className="text-gold" />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
